import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { WRAP_COLORS } from "../data";
import { useI18n } from "../i18n";

const Card = ({ color, i, t, onQuote }) => (
  <motion.div
    data-testid={`color-card-${color.id}`}
    initial={{ opacity: 0, y: 50 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay: i * 0.07, ease: [0.16, 1, 0.3, 1] }}
    className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0F1115] hover:border-cyan-400/40 transition-colors duration-500"
  >
    <div className="relative overflow-hidden aspect-[3/4]">
      <img
        src={color.image}
        alt={t(`colors.items.${color.id}.name`)}
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0F1115] via-transparent to-transparent opacity-80" />
      <span className="absolute top-4 left-4 flex items-center gap-2 font-mono-accent text-[10px] uppercase tracking-[0.2em] bg-black/50 backdrop-blur px-3 py-1.5 rounded-full border border-white/10">
        <span className="h-3 w-3 rounded-full border border-white/20" style={{ backgroundColor: color.swatch }} />
        {t(`colors.items.${color.id}.tag`)}
      </span>
    </div>

    <div className="p-5 flex flex-col flex-1">
      <h3 className="font-display font-600 uppercase tracking-tight text-base leading-tight group-hover:text-cyan transition-colors duration-300">
        {t(`colors.items.${color.id}.name`)}
      </h3>
      <p className="text-neutral-400 text-xs font-light mt-2 flex-1">
        {t(`colors.items.${color.id}.desc`)}
      </p>
      <button
        data-testid={`color-cta-${color.id}`}
        onClick={onQuote}
        className="mt-4 inline-flex items-center justify-between gap-2 text-xs font-medium text-cyan border border-cyan-400/30 rounded-xl px-3 py-2.5 hover:bg-cyan-400 hover:text-black transition-colors duration-300"
      >
        {t("colors.cta")}
        <ArrowUpRight size={14} />
      </button>
    </div>
  </motion.div>
);

export const WrapColors = ({ onQuote }) => {
  const { t } = useI18n();
  return (
    <section id="teintes" data-testid="colors-section" className="relative py-28 md:py-36 px-6 md:px-10 bg-[#070809] border-y border-cyan-500/10">
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
          <div>
            <span className="font-mono-accent text-xs uppercase tracking-[0.3em] text-cyan">{t("colors.tag")}</span>
            <h2 className="font-display font-700 uppercase tracking-tighter text-4xl md:text-6xl mt-4 leading-none">
              {t("colors.title1")} <span className="text-cyan neon-text-glow">{t("colors.title2")}</span>
            </h2>
          </div>
          <p className="max-w-sm text-neutral-400 font-light">{t("colors.subtitle")}</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5">
          {WRAP_COLORS.map((color, i) => (
            <Card key={color.id} color={color} i={i} t={t} onQuote={onQuote} />
          ))}
        </div>
      </div>
    </section>
  );
};
